/*
 * rs-gift.js — RESET launch-month gift-with-purchase. Layer B.
 * SOURCE OF TRUTH: tools/seo/theme-bundle/assets/rs-gift.js
 * Config + gate come from snippets/rs-gift-promo.liquid. If the window is shut
 * that snippet emits nothing, window.RS_GIFT is undefined, and this file is
 * never even requested.
 *
 * TWO JOBS
 *   1. Auto-add the free 1-month jar OF THE SAME PRODUCT when a qualifying pack
 *      is in the cart. Shopify's Buy X Get Y NEVER adds the Y item itself — per
 *      Shopify's own docs, "the free or discounted 'get' item is never
 *      automatically added to the cart". Without this the customer checks out
 *      without the gift.
 *   2. Keep the free-shipping progress bar in step with the cart.
 *
 * ── E1: THE CHARGE GUARD IS THE WHOLE SAFETY STORY ──────────────────────
 * Counsel 2026-09-10 rates this halt-level, and it is worse than the defect it
 * replaces: under the old design the customer chose to add the jar, so a failed
 * discount meant they bought something they picked. Now WE put a 139-169 ₪ line
 * in their cart. Every one of these is real: the discount was never created,
 * paused, or expired; max-uses-per-order already consumed; a second candidate
 * jar took the allocation under Shopify's lower-priced-item rule leaving ours
 * charged; the gift variant overselling at zero stock; a client clock outside
 * the window. So: add, RE-READ the cart, and REMOVE the line again unless it
 * came back fully discounted. We never assume the discount worked — we read it.
 *
 * ── E7: THE DECLINE IS A CART ATTRIBUTE, NOT sessionStorage ─────────────
 * Counsel: sessionStorage would let a NEW TAB re-add a jar the customer
 * deliberately removed, which is precisely the behaviour that earns the
 * dark-pattern label. The decline therefore rides on the cart itself
 * (`_rs_gift_declined`), so it survives tabs, reloads and devices sharing the
 * cart. sessionStorage is still used, but only to stop a failed attempt
 * retrying in a loop within one session — a different job.
 *
 * ── /cart.js CAN RETURN HTML, BUT DO NOT SNIFF THE HEADER ───────────────
 * Under the bot challenge /cart.js answers 429 with an HTML body. Calling
 * .json() on that throws and kills the handler, so every response goes through
 * readJson() — which checks res.ok and lets .catch() absorb a body that will
 * not parse.
 *
 * It must NOT also gate on content-type. Shopify serves a healthy /cart.js as
 * `text/javascript`, which does not contain the substring "json", so a
 * content-type gate rejects every good response as well as the bad ones. That
 * bug shipped here and made the whole module inert: no cart read, no add, no
 * rollback, and no error to show for it.
 */
(function () {
  'use strict';

  var C = window.RS_GIFT;
  if (!C || !C.live) return;

  var TRIED = 'rs_gift_tried';       /* session: loop guard only */
  var SAID = 'rs_gift_said';         /* session: announce once after a real add */
  var DECLINED = '_rs_gift_declined'; /* CART attribute: the durable opt-out */
  var ADDED = '_rs_gift_added';       /* CART attribute: we put a jar in */
  var SECTIONS = ['cart-drawer', 'cart-icon-bubble', 'main-cart-items', 'main-cart-footer'];

  function sget(k) { try { return sessionStorage.getItem(k) === '1'; } catch (e) { return false; } }
  function sset(k) { try { sessionStorage.setItem(k, '1'); } catch (e) { /* private mode */ } }
  function sclear(k) { try { sessionStorage.removeItem(k); } catch (e) { } }

  /* Never call .json() unconditionally — a 429 bot challenge is HTML. */
  function readJson(res) {
    if (!res || !res.ok) return null;
    /* NO content-type gate. Shopify serves /cart.js as `text/javascript`, not
       `application/json` — measured on the live storefront. The gate that used
       to sit here tested for the substring "json", which "text/javascript" does
       not contain, so EVERY cart read returned null: getCart() gave null,
       autoAdd() bailed at `if (!cart)`, and because post() runs through here
       too, the add and the rollback were dead as well. The module was inert and
       looked healthy — no errors, no add, so nothing to notice.
       res.json().catch() already covers the case this was written for: a body
       that will not parse, such as the 429 bot challenge's HTML. The !res.ok
       check above catches the 429 itself. Neither needs a header sniff. */
    return res.json().catch(function () { return null; });
  }

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function getCart() {
    return fetch(root() + 'cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then(readJson).catch(function () { return null; });
  }

  function post(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body)
    }).then(readJson).catch(function () { return null; });
  }

  function setAttrs(attrs) { return post(root() + 'cart/update.js', { attributes: attrs }); }

  /* ---------------------------------------------------------------- state */

  function scan(cart) {
    var out = { pack: null, gift: null };
    if (!cart || !cart.items) return out;
    for (var i = 0; i < cart.items.length; i++) {
      var it = cart.items[i];
      if (!out.pack && C.map[String(it.variant_id)]) out.pack = C.map[String(it.variant_id)];
      if (!out.gift && C.gifts.indexOf(it.variant_id) !== -1) out.gift = it;
    }
    return out;
  }

  function attr(cart, k) {
    return cart && cart.attributes ? cart.attributes[k] : null;
  }

  /* 2026-10-07: add the GIFT line without Shopify publishing product_added_to_cart. That event reaches Meta/GA4/Klaviyo with the LIST price (169),
     inflating AddToCart. navigator.sendBeacon bypasses the storefront's fetch/XHR instrumentation; a beacon has no response, so poll the cart for the line.
     If sendBeacon is unavailable or refuses, fall back to the old fetch (event fires). The charge guard below is unchanged. */
  function quietAdd(id) {
    var body = JSON.stringify({ items: [{ id: id, quantity: 1 }] });
    var sent = false;
    try { sent = !!(navigator.sendBeacon && navigator.sendBeacon(root() + 'cart/add.js', new Blob([body], { type: 'application/json' }))); } catch (e) { }
    if (!sent) return post(root() + 'cart/add.js', JSON.parse(body));
    var tries = 0;
    return new Promise(function (resolve) {
      (function poll() {
        getCart().then(function (c) {
          if (scan(c).gift) return resolve({ ok: true });
          if (++tries >= 8) return resolve(null);
          setTimeout(poll, 400);
        });
      })();
    });
  }

  /* ------------------------------------------------------------- auto-add */

  function autoAdd() {
    return getCart().then(function (cart) {
      if (!cart) return false;

      /* AN EMPTY CART IS A FRESH START, NOT THE CONTINUATION OF A DECISION.
         Shopify keeps cart.attributes when the cart is emptied — verified on
         both routes, /cart/clear.js and zeroing every line. So a customer who
         took the gift, emptied the cart and started again carried
         _rs_gift_declined with them and was refused the gift on their next
         pack, often a BIGGER one. Clear the record here, and the session's
         loop guard with it, so the next load behaves like a first visit.

         The TRIED check has to live below this rather than above it: it used
         to short-circuit before any network call, which meant we never read
         the cart and so could never notice it had been emptied. */
      if (cart.item_count === 0) {
        sclear(TRIED);
        if (attr(cart, ADDED) || attr(cart, DECLINED)) {
          return setAttrs({ _rs_gift_added: '', _rs_gift_declined: '' })
            .then(function () { return false; });
        }
        return false;
      }

      if (sget(TRIED)) return false;
      var s = scan(cart);

      /* The customer removed a jar we added -> record a DURABLE decline and
         never put it back, in this tab or any other. */
      if (attr(cart, ADDED) && !s.gift) {
        return setAttrs({ _rs_gift_declined: '1', _rs_gift_added: '' }).then(function () { return false; });
      }

      if (attr(cart, DECLINED) === '1') return false;
      if (!s.pack || s.gift) return false;   /* nothing to do, or already there */

      sset(TRIED);                            /* before the request, never after */

      return quietAdd(s.pack)
        .then(function (added) {
          if (!added) return false;
          return getCart().then(function (after) {
            var g = scan(after).gift;

            /* E1 — THE CHARGE GUARD. Only a line that came back at zero stays. */
            if (!g || g.final_line_price !== 0) {
              if (g) {
                return post(root() + 'cart/change.js', { id: g.key, quantity: 0 })
                  .then(function () { return false; });
              }
              return false;
            }
            sset(SAID);
            return setAttrs({ _rs_gift_added: '1' }).then(function () { return true; });
          });
        });
    }).catch(function () { return false; });
  }

  /* --------------------------------------------------------- section swap */

  /* Re-render the cart drawer from the Section Rendering API (same approach as the theme's own upsell add in cart-drawer.js). */
  function refreshDrawer() {
    var host = document.getElementById('CartDrawer');
    if (!host) return Promise.resolve();
    return fetch(root() + '?sections=cart-drawer,cart-icon-bubble', { credentials: 'same-origin' })
      .then(readJson)
      .then(function (data) {
        if (!data || !data['cart-drawer']) return;
        var fresh = new DOMParser().parseFromString(data['cart-drawer'], 'text/html').querySelector('#CartDrawer');
        if (!fresh) return;
        host.innerHTML = fresh.innerHTML;
        var drawer = document.querySelector('cart-drawer');
        var ov = host.querySelector('#CartDrawer-Overlay');
        if (ov && drawer && typeof drawer.close === 'function') ov.addEventListener('click', drawer.close.bind(drawer));
        if (drawer) drawer.classList.remove('is-empty');
        var bub = document.getElementById('cart-icon-bubble');
        if (bub && data['cart-icon-bubble']) {
          var b = new DOMParser().parseFromString(data['cart-icon-bubble'], 'text/html').querySelector('.shopify-section');
          if (b) bub.innerHTML = b.innerHTML;
        }
        if (drawer && typeof drawer._refreshUpsellCards === 'function') drawer._refreshUpsellCards();
        paint();
        announce();
      }).catch(function () { /* leave the drawer as it is */ });
  }

  function refresh() {
    var ids = SECTIONS.filter(function (id) { return document.getElementById('shopify-section-' + id); });
    if (!ids.length) {
      if (location.pathname.indexOf('/cart') === 0) { location.reload(); return Promise.resolve(); }
      return refreshDrawer();   /* 2026-10-07: AJAX add-to-cart opens the drawer BEFORE the gift line exists; re-render it once the gift is in */
    }
    return fetch('?sections=' + ids.join(','), { credentials: 'same-origin' })
      .then(readJson)
      .then(function (data) {
        if (!data) { if (location.pathname.indexOf('/cart') === 0) location.reload(); return; }
        ids.forEach(function (id) {
          var host = document.getElementById('shopify-section-' + id);
          if (host && data[id]) {
            var fresh = new DOMParser().parseFromString(data[id], 'text/html')
              .getElementById('shopify-section-' + id);
            if (fresh) host.innerHTML = fresh.innerHTML;
          }
        });
        paint();
        announce();
      }).catch(function () { /* leave the page as it is */ });
  }

  /* ---------------------------------------------------------- E2 announce */
  /* The cart must SAY the jar was added and that it can be removed. Counsel
     makes this a condition of auto-add staying outside the dark-pattern
     doctrine. role="status" also covers the accessibility side: the content
     changed without the user asking. */
  function announce() {
    if (!sget(SAID)) return;
    var anchor = document.querySelector('[data-rs-ship]') || document.querySelector('[data-rs-gift-notice]');
    if (!anchor || document.querySelector('[data-rs-gift-said]')) return;
    var p = document.createElement('p');
    p.className = 'rs-gift-said';
    p.setAttribute('role', 'status');
    p.setAttribute('data-rs-gift-said', '');
    p.textContent = C.strings.added;
    anchor.parentNode.insertBefore(p, anchor);
    sclear(SAID);
  }

  /* --------------------------------------------------- free-shipping bar */

  function money(cents) {
    return '₪' + (cents / 100).toFixed(cents % 100 === 0 ? 0 : 2);
  }

  function paint() {
    var bars = document.querySelectorAll('[data-rs-ship]');
    if (!bars.length) return;
    getCart().then(function (cart) {
      if (!cart) return;
      /* total_price is AFTER discounts — the same basis Shopify uses for a
         price-based free-shipping threshold. */
      var left = C.threshold - cart.total_price;
      var pct = Math.min(100, Math.round((cart.total_price / C.threshold) * 100));
      for (var i = 0; i < bars.length; i++) {
        var bar = bars[i];
        var fill = bar.querySelector('[data-rs-ship-fill]');
        var label = bar.querySelector('[data-rs-ship-label]');
        if (fill) fill.style.width = pct + '%';
        if (label) {
          label.textContent = left <= 0 ? C.strings.reached
            : C.strings.progress.replace('__AMT__', money(left));
        }
        bar.classList.toggle('is-reached', left <= 0);
      }
    });
  }

  /* ----------------------------------------------------------------- boot */

  function run() {
    return autoAdd().then(function (changed) {
      if (changed) return refresh();
      paint();
      announce();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }

  /* The PDP buybox calls this after its own add-to-cart succeeds, so a pack
     added without a page load still gets its gift. */
  C.check = run;

  document.addEventListener('rs:cart:updated', function () { paint(); });
})();
